import { Children } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Skeleton, SxProps } from '@mui/material';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useGoBack } from 'hooks/useGoBack';
import { useGetOne, useGetRecordId, useResourceContext } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box } from 'shared/mui/Box';
import { CardActionsButtons, CardActionsButtonsProps } from 'shared/mui/Card';
import { CardAdditionalButtons } from 'shared/mui/Card/CardAdditionalButtons/CardAdditionalButtons';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { IconButton } from 'shared/mui/IconButtons';
import { PageHeader, PageHeaderTitleProps } from 'shared/mui/PageHeader';
import { SimpleShowLayout } from 'shared/react-admin/SimpleShowLayout/SimpleShowLayout';

import { ShowStyled } from './ShowPage.styled';

export type ShowPageProps = {
  headerTitle?: PageHeaderTitleProps;
  title: string;
  listName: string;
  children: React.ReactNode;
  leftActionsSlot?: JSX.Element | false | (JSX.Element | false)[];
  rightActionsSlot?: JSX.Element | false | (JSX.Element | false)[];
  actions?: (React.ReactElement | false) | (React.ReactElement | false)[];
  additionalActions?: React.ReactElement[] | false;
  sx?: SxProps;
  resource?: string;
  cardActionsButtonsProps?: Omit<CardActionsButtonsProps, 'leftActionsSlot' | 'rightActionsSlot'>;
  skeleton?: React.ReactNode;
};

const renderActionsSkeleton = (slot: ShowPageProps['rightActionsSlot']): React.ReactNode =>
  Children.toArray(slot).map((_, index) => (
    <Skeleton height={36} key={index} variant="rounded" width={120} />
  ));

export const ShowPage: React.FC<ShowPageProps> = ({
  title,
  listName,
  children,
  headerTitle,
  actions,
  leftActionsSlot,
  rightActionsSlot,
  additionalActions,
  sx,
  resource,
  cardActionsButtonsProps,
  skeleton: pageSkeleton,
}) => {
  const { goBack } = useGoBack();
  const navigate = useNavigate();
  const { createPathWithQuery } = useCreatePathWithQuery();
  const id = useGetRecordId();
  const resourceName = useResourceContext({ resource }) ?? '';
  const { isFetchedAfterMount } = useGetOne(resourceName, { id }, { enabled: false });
  const skeleton = !isFetchedAfterMount && pageSkeleton;

  const handleGoBack = (): void => {
    if (resource) {
      navigate(createPathWithQuery({ resource, type: 'list' }));

      return;
    }

    goBack();
  };

  const content = (
    <>
      <CardHeader
        appendActions={
          skeleton && actions ? <Skeleton height={32} variant="circular" width={32} /> : actions
        }
        prependActions={
          <IconButton onClick={handleGoBack}>
            <ArrowBackIcon />
          </IconButton>
        }
        title={title}
        titleSize="smallBold"
      />
      {additionalActions && additionalActions?.length > 0 && (
        <CardAdditionalButtons>{additionalActions}</CardAdditionalButtons>
      )}
      <CardContent sx={{ paddingTop: '16px !important' }}>{skeleton || children}</CardContent>
      <CardActionsButtons
        leftActionsSlot={skeleton ? renderActionsSkeleton(leftActionsSlot) : leftActionsSlot}
        rightActionsSlot={skeleton ? renderActionsSkeleton(rightActionsSlot) : rightActionsSlot}
        {...cardActionsButtonsProps}
      />
    </>
  );

  return (
    <Box>
      <PageHeader
        breadCrumbs={{
          currentPage: title,
          listName: listName,
          resource,
        }}
        headerTitle={headerTitle}
      />
      <ShowStyled actions={false} resource={resource} sx={sx}>
        {skeleton ? content : <SimpleShowLayout>{content}</SimpleShowLayout>}
      </ShowStyled>
    </Box>
  );
};
